import { useCallback, useEffect, useState } from 'react';
import Card from '../../../components/atoms/card';
import MovementService, { IMovement } from '../../../services/Movement.service';
import Input from '../../../components/atoms/input';
import InputEmails from '../../../components/molecules/input-emails';
import SVGIcon from '../../../components/atoms/icons';
import Button from '../../../components/atoms/button';
import InputCalendar from '../../../components/molecules/input-calendar';
import Select from '../../../components/atoms/select';
import { useRouteLoaderData } from 'react-router-dom';
import UserService, { IUser } from '../../../services/User.service';
import ClientService, { IClient } from '../../../services/Client.service';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../utils/types/editable';
import YardService, { IYard } from '../../../services/Yard.service';
import ShipService, { IShip } from '../../../services/Ship.service';
import { IContainer } from '../../../services/Container.service';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import SheetUpload from '../../../components/organisms/sheet-upload';
import Label from '../../../components/atoms/text/label';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import Text from '../../../components/atoms/text';
import Table, { TableColumn } from '../../../components/organisms/table';
import Actions from '../../../components/molecules/actions';

interface MovementForm extends Omit<IMovement, 'owner' | 'containers'> {
	containers: (Partial<IContainer> & { dry?: boolean })[];
	owner: string[];
	lc: boolean;
	cb: boolean;
	dry: boolean;
	bySheet: boolean;
}

const movementInitialState: MovementForm = {
	containers: [],
	date: new Date(),
	isArrived: true,
	travelCode: '',
	yard: {},
	ship: {},
	owner: [],
	lc: true,
	cb: true,
	dry: true,
	bySheet: false,
};

interface SheetHeader {
	header: string[];
	position: string;
	container: string;
	owner: string;
	temperature: string;
	humidity: string;
	ventilation: string;
	atmosferaControlada: string;
	nivelCo2: string;
	carrier: string;
	lc: string;
	cb: string;
	dry: string;
}

const MovementAuto = () => {
	const { t } = useTranslation();
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [form, setForm] = useState<MovementForm>(movementInitialState);

	const [yards, setYards] = useState<IYard[]>([]);
	const [ships, setShips] = useState<IShip[]>([]);
	const [error, setError] = useState<string>('');
	const [sheetIndexes, setSheetIndexes] = useState({
		header: -1,
		position: -1,
		container: -1,
		owner: -1,
		lc: -1,
		cb: -1,
		dry: -1,
		temperature: -1,
		humidity: -1,
		ventilation: -1,
		atmosferaControlada: -1,
		nivelCo2: -1,
		carrier: -1,
	});
	const [sheetHeader, setSheetHeader] = useState<SheetHeader>({
		header: [],
		position: '',
		container: '',
		owner: '',
		lc: '',
		cb: '',
		dry: '',
		temperature: '',
		humidity: '',
		ventilation: '',
		atmosferaControlada: '',
		nivelCo2: '',
		carrier: '',
	});
	const [headerOptions, setHeaderOptions] = useState<string[]>([]);
	const [sheetData, setSheetData] = useState<any[][]>([]);

	const onActionsEditClick = (row: Partial<IContainer>) => {};

	const columns: TableColumn<MovementForm['containers'][number]>[] = [
		{
			key: 'label',
			label: t('port_management.container'),
			children: (row, index) => (
				<Input
					name="label"
					value={row.label}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.container')}
					required
				/>
			),
		},
		{
			key: 'temperature',
			label: t('port_management.containers.set_point'),
			children: (row, index) => (
				<Input
					name="temperature"
					value={row.temperature}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.temperature')}
					required={!row.dry}
				/>
			),
		},
		{
			key: 'owner',
			label: t('port_management.containers.owner'),
			children: (row, index) => (
				<Input
					name="owner"
					value={row.owner}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.owner')}
					required
				/>
			),
		},
		{
			key: 'humidity',
			label: t('port_management.containers.humidity'),
			children: (row, index) => (
				<Input
					name="humidity"
					value={row.humidity || ''}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.humidity')}
					type="number"
				/>
			),
		},
		{
			key: 'ventilation',
			label: t('port_management.containers.ventilation'),
			children: (row, index) => (
				<Input
					name="ventilation"
					value={row.ventilation || ''}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.ventilation')}
					type="number"
				/>
			),
		},
		{
			key: 'nivelCo2',
			label: t('port_management.containers.nivel_co2'),
			children: (row, index) => (
				<Input
					name="nivelCo2"
					value={row.nivelCo2 || ''}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.nivel_co2')}
					type="number"
				/>
			),
		},
		{
			key: 'atmosferaControlada',
			label: t('port_management.containers.atmosfera_controlada'),
			children: (row, index) => (
				<Label
					className="flex items-center gap-2"
					htmlFor={`atmosferaControlada-${index}`}
				>
					<input
						id={`atmosferaControlada-${index}`}
						type="checkbox"
						checked={!!row.atmosferaControlada}
						onChange={() => handleAtmosferaControlada(index)}
					/>
					{t('misc.yes')}
				</Label>
			),
		},
		{
			key: 'position',
			label: t('port_management.containers.position'),
			children: (row, index) => (
				<Input
					name="position"
					value={row.position}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.containers.position')}
					required
				/>
			),
		},
		{
			key: 'carrier',
			label: t('port_management.carrier'),
			children: (row, index) => (
				<Input
					name="carrier"
					value={row.carrier}
					onChange={(e) => handleContainer(e, index)}
					placeholder={t('port_management.carrier')}
					required
				/>
			),
		},

		{
			key: 'actions',
			label: t('misc.actions'),
			children: (_, index) => (
				<Actions onDeleteClick={() => removeContainer(index)} />
			),
		},
	];

	const loadContainers = useCallback(async () => {
		if (sheetData.length == 0) return;
		setForm((prev) => ({
			...prev,
			containers: [],
			lc: false,
			cb: false,
			dry: false,
		}));
		const options: string[] = [];
		let HEADER_INDEX = -1;
		for (let i = 0; i < sheetData.length; i++) {
			const row = sheetData[i];
			if (row[0] === 'IT') {
				HEADER_INDEX = i;
				setSheetIndexes((prev) => ({ ...prev, header: i }));
				setSheetHeader((prev) => ({ ...prev, header: row }));
				for (let j = 0; j < row.length; j++) {
					switch (row[j]) {
						case 'PSN':
							setSheetIndexes((prev) => ({
								...prev,
								position: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								position: row[j],
							}));
							break;
						case 'LC/\nCB':
							setSheetIndexes((prev) => ({ ...prev, lc: j }));
							setSheetIndexes((prev) => ({ ...prev, cb: j }));
							setSheetHeader((prev) => ({ ...prev, lc: row[j] }));
							setSheetHeader((prev) => ({ ...prev, cb: row[j] }));
							break;
						case 'CONSIG':
							setSheetIndexes((prev) => ({ ...prev, owner: j }));
							setSheetHeader((prev) => ({
								...prev,
								owner: row[j],
							}));
							break;
						case 'CONT.\nNRO':
							setSheetIndexes((prev) => ({
								...prev,
								container: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								container: row[j],
							}));
							break;
						case 'DRY':
							setSheetIndexes((prev) => ({ ...prev, dry: j }));
							setSheetHeader((prev) => ({ ...prev, dry: row[j] }));
							break;
						case 'SET POINT':
							setSheetIndexes((prev) => ({
								...prev,
								temperature: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								temperature: row[j],
							}));
							break;
						case 'HUMIDITY':
						case 'UMIDADE':
							setSheetIndexes((prev) => ({
								...prev,
								humidity: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								humidity: row[j],
							}));
							break;
						case 'VENTILATION':
						case 'VENTILAÇÃO':
							setSheetIndexes((prev) => ({
								...prev,
								ventilation: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								ventilation: row[j],
							}));
							break;
						case 'CONTROLLED ATMOSPHERE':
						case 'ATMOSFERA CONTROLADA':
							setSheetIndexes((prev) => ({
								...prev,
								atmosferaControlada: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								atmosferaControlada: row[j],
							}));
							break;
						case 'CO2 LEVEL':
						case 'NIVEL CO2':
						case 'NÍVEL CO2':
							setSheetIndexes((prev) => ({
								...prev,
								nivelCo2: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								nivelCo2: row[j],
							}));
							break;
						case 'CARRIER':
							setSheetIndexes((prev) => ({
								...prev,
								carrier: j,
							}));
							setSheetHeader((prev) => ({
								...prev,
								carrier: row[j],
							}));
							break;
						default:
							break;
					}
				}
			}
			options.push(row.join(' - '));
		}

		setHeaderOptions(options);
		setForm((prev) => ({ ...prev, bySheet: true }));
	}, [sheetData]);

	const handleBooleanValue = (value: any) => {
		const valueUpdated = String(value || '')
			.trim()
			.toLowerCase();
		return ['1', 'true', 'sim', 'yes', 'y', 's', 'x'].includes(
			valueUpdated
		);
	};

	useEffect(() => {
		loadContainers();
	}, [loadContainers]);

	useEffect(() => {
		if (sheetIndexes.header < 0) return;
		const containers: MovementForm['containers'] = [];
		for (let i = sheetIndexes.header + 1; i < sheetData.length; i++) {
			const row = sheetData[i];

			if (row.length === 0) {
				break;
			}

			const containerDry =
				String(row[sheetIndexes.dry] || '').trim().toUpperCase() === 'DRY';
			let temperature = String(row[sheetIndexes.temperature] ?? '').trim();
			if (containerDry) temperature = '';
			else if (!temperature || temperature.toUpperCase() === 'DRY') continue;
			else temperature = temperature.replace(/[^0-9\-,.]/g, '');

			if (
				containerDry
					? form.dry
					: (row[sheetIndexes.lc] == 'LC' && form.lc) ||
					  (row[sheetIndexes.cb] == 'CB' && form.cb)
			)
				containers.push({
					dry: containerDry,
					position:
						sheetIndexes.position > -1
							? String(row[sheetIndexes.position] || '')
							: '',
					label:
						sheetIndexes.container > -1
							? row[sheetIndexes.container] || ''
							: '',
					temperature,
					humidity:
						sheetIndexes.humidity > -1
							? String(row[sheetIndexes.humidity] || '')
							: '',
					ventilation:
						sheetIndexes.ventilation > -1
							? String(row[sheetIndexes.ventilation] || '')
							: '',
					atmosferaControlada:
						sheetIndexes.atmosferaControlada > -1
							? handleBooleanValue(
									row[sheetIndexes.atmosferaControlada]
							  )
							: false,
					nivelCo2:
						sheetIndexes.nivelCo2 > -1
							? String(row[sheetIndexes.nivelCo2] || '')
							: '',
					carrier:
						sheetIndexes.carrier > -1
							? row[sheetIndexes.carrier] || ''
							: '',
					owner:
						sheetIndexes.owner > -1
							? row[sheetIndexes.owner] || ''
							: '',
				});
		}
		setForm((prev) => ({ ...prev, containers }));
	}, [
		form.cb,
		form.dry,
		sheetIndexes.header,
		sheetIndexes.dry,
		form.lc,
		sheetIndexes.position,
		sheetIndexes.carrier,
		sheetIndexes.container,
		sheetIndexes.humidity,
		sheetIndexes.lc,
		sheetIndexes.nivelCo2,
		sheetIndexes.cb,
		sheetIndexes.owner,
		sheetIndexes.temperature,
		sheetIndexes.ventilation,
		sheetIndexes.atmosferaControlada,
	]);

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;
		const response = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setYards(response.data);
	}, [accountId]);

	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	const fetchShips = useCallback(async () => {
		if (!accountId) return;
		const response = await ShipService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setShips(response.data);
	}, [accountId]);

	useEffect(() => {
		fetchShips();
	}, [fetchShips]);

	const addContainer = () => {
		const containers = [...form.containers];
		containers.push({
			temperature: '',
			humidity: '',
			ventilation: '',
			atmosferaControlada: false,
			nivelCo2: '',
			label: '',
			position: '',
			carrier: '',
			owner: '',
		});
		setForm((prev) => ({ ...prev, containers }));
	};

	const removeContainer = (index?: number) => {
		if (index === undefined) return;
		const containers = [...form.containers];
		containers.splice(index, 1);
		setForm((prev) => ({ ...prev, containers }));
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleYard = (e: EditableChangeEvent) => {
		const yard = yards.find((item) => item.name === e.target.value);
		if (!yard) return;
		handleInput({ target: { name: 'yard', value: yard } });
	};

	const handleShip = (e: EditableChangeEvent) => {
		const ship = ships.find((item) => item.name === e.target.value);
		if (!ship) return;
		handleInput({ target: { name: 'ship', value: ship } });
	};

	const handleContainer = (
		e: React.ChangeEvent<HTMLInputElement>,
		index?: number
	) => {
		if (index === undefined) return;
		const containers = [...form.containers];
		containers[index][e.target.name as keyof IContainer] = e.target.value;
		handleInput({ target: { name: 'containers', value: containers } });
	};

	const handleAtmosferaControlada = (index?: number) => {
		if (index === undefined) return;
		const containers = [...form.containers];
		containers[index].atmosferaControlada =
			!containers[index].atmosferaControlada;
		handleInput({ target: { name: 'containers', value: containers } });
	};

	const handleDate = (date: Date) => {
		handleInput({ target: { name: 'date', value: date } });
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const emailInput = e.currentTarget.querySelector<HTMLInputElement>(
			'input[type="email"][name="owner"]'
		);
		if (emailInput && !emailInput.reportValidity()) return;
		if (
			!form.yard ||
			!form.ship ||
			!form.yard.id ||
			!form.date ||
			!form.travelCode
		) {
			setError('alert.error.mandatory');
			return;
		}

		const { lc, cb, dry, bySheet, ...data } = form;
		const response = await MovementService.create(
			{
				...data,
				containers: data.containers.map(({ dry, ...container }) => container),
			},
			setError
		);
		if (response) {
			NotificationToast.Success(
				`${t('movement.title')} ${t('alert.success.create')}`
			);
			setForm(movementInitialState);
			setSheetData([]);
		}
	};

	const onSheetUpload = (data: any[][]) => {
		setSheetData(data);
	};

	const handleHeader = (e: EditableChangeEvent) => {
		const indexHeader = headerOptions.findIndex(
			(header) => header === e.target.value
		);
		if (indexHeader === -1) return;
		setSheetIndexes(() => ({
			header: indexHeader,
			position: -1,
			container: -1,
			owner: -1,
			lc: -1,
			cb: -1,
			dry: -1,
			temperature: -1,
			humidity: -1,
			ventilation: -1,
			atmosferaControlada: -1,
			nivelCo2: -1,
			carrier: -1,
		}));
		setSheetHeader(() => ({
			header: headerOptions[indexHeader].split(' - '),
			carrier: '',
			container: '',
			lc: '',
			cb: '',
			dry: '',
			owner: '',
			position: '',
			temperature: '',
			humidity: '',
			ventilation: '',
			atmosferaControlada: '',
			nivelCo2: '',
		}));
		setForm((prev) => ({ ...prev, containers: [] }));
	};

	const handleHeaderContent = (e: EditableChangeEvent) => {
		const index = sheetHeader.header.findIndex(
			(header) => header === e.target.value
		);
		if (index == -1) return;
		setSheetHeader((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
		setSheetIndexes((prev) => ({ ...prev, [e.target.name]: index }));
		setForm((prev) => ({ ...prev, containers: [] }));
	};

	return (
		<Card className="flex flex-col flex-grow">
			<form
				className="flex flex-col flex-grow p-4 gap-6"
				autoComplete="off"
				noValidate
				onSubmit={handleSubmit}
			>
				<div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 items-start">
					<Select
						name="yard"
						value={form.yard.name || ''}
						onChange={handleYard}
						label={t('port_management.yard')}
						placeholder={t('port_management.yard')}
						options={yards.map((item) => item.name)}
						required
					/>
					<Select
						name="ship"
						value={form.ship.name || ''}
						onChange={handleShip}
						label={t('port_management.ship')}
						placeholder={t('port_management.ship')}
						options={ships.map((item) => item.name)}
						required
					/>
					<Input
						name="travelCode"
						value={form.travelCode}
						onChange={handleInput}
						label={t('movement.travel_code')}
						placeholder={t('movement.travel_code')}
						required
					/>
					<InputCalendar
						date={form.date}
						label={t('date.retroactive')}
						onDateChange={handleDate}
						placeholder={t('date.retroactive')}
						required
					/>
					<InputEmails
						name="owner"
						value={form.owner}
						onChange={handleInput}
						label={t('movement.client_email')}
					/>
				</div>
				<a
					href="/sheet_template_v2.xlsx"
					download="sheet_template_v2.xlsx"
					className="w-fit place-self-end"
				>
					<Button>
						<SVGIcon
							icon="Download"
							className="h-4 w-auto stroke-white"
						/>
						{t('misc.download_template')}
					</Button>
				</a>
				<div className="flex flex-col gap-y-6 overflow-hidden">
					<SheetUpload onSheetUpload={onSheetUpload} />
					{form.bySheet && (
						<>
							<Text className="text-primary" as="h4">
								{t('movement.header_info')}
							</Text>
							<div className="flex flex-col gap-4">
								<div className="flex gap-4">
									<Label
										className="flex items-center gap-2"
										htmlFor="lc"
									>
										<input
											id="lc"
											type="checkbox"
											checked={form.lc}
											onChange={() =>
												setForm((prev) => ({
													...prev,
													lc: !prev.lc,
												}))
											}
										/>
										LC
									</Label>
									<Label
										className="flex items-center gap-2"
										htmlFor="cb"
									>
										<input
											id="cb"
											type="checkbox"
											checked={form.cb}
											onChange={() =>
												setForm((prev) => ({
													...prev,
													cb: !prev.cb,
												}))
											}
										/>
										CB
									</Label>
									<Label
										className="flex items-center gap-2"
										htmlFor="dry"
									>
										<input
											id="dry"
											type="checkbox"
											checked={form.dry}
											onChange={() =>
												setForm((prev) => ({
													...prev,
													dry: !prev.dry,
												}))
											}
										/>
										DRY
									</Label>
								</div>
								<Select
									name="header"
									placeholder={t('movement.header')}
									label={t('movement.header')}
									value={sheetHeader.header.join(' - ')}
									title={sheetHeader.header.join(' - ')}
									onChange={(e) => handleHeader(e)}
									optionClassName="text-xs"
									placeholderClassName="text-xs text-ellipsis overflow-hidden"
									options={headerOptions}
								/>
								<div className="grid grid-cols:1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
									<Select
										name="container"
										placeholder={t(
											'port_management.container'
										)}
										label={t('port_management.container')}
										value={sheetHeader.container}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="temperature"
										placeholder={t(
											'port_management.containers.set_point'
										)}
										label={t(
											'port_management.containers.set_point'
										)}
										value={sheetHeader.temperature}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="lc"
										placeholder="LC"
										label="LC"
										value={sheetHeader.lc}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="cb"
										placeholder="CB"
										label="CB"
										value={sheetHeader.cb}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="dry"
										placeholder="DRY"
										label="DRY"
										value={sheetHeader.dry}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="owner"
										placeholder={t(
											'port_management.containers.owner'
										)}
										label={t(
											'port_management.containers.owner'
										)}
										value={sheetHeader.owner}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="humidity"
										placeholder={t(
											'port_management.containers.humidity'
										)}
										label={t(
											'port_management.containers.humidity'
										)}
										value={sheetHeader.humidity}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="ventilation"
										placeholder={t(
											'port_management.containers.ventilation'
										)}
										label={t(
											'port_management.containers.ventilation'
										)}
										value={sheetHeader.ventilation}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="atmosferaControlada"
										placeholder={t(
											'port_management.containers.atmosfera_controlada'
										)}
										label={t(
											'port_management.containers.atmosfera_controlada'
										)}
										value={
											sheetHeader.atmosferaControlada
										}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="nivelCo2"
										placeholder={t(
											'port_management.containers.nivel_co2'
										)}
										label={t(
											'port_management.containers.nivel_co2'
										)}
										value={sheetHeader.nivelCo2}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="carrier"
										placeholder={t(
											'port_management.carrier'
										)}
										label={t('port_management.carrier')}
										value={sheetHeader.carrier}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
									<Select
										name="position"
										placeholder={t(
											'port_management.containers.position'
										)}
										label={t(
											'port_management.containers.position'
										)}
										value={sheetHeader.position}
										onChange={(e) => handleHeaderContent(e)}
										options={sheetHeader.header}
									/>
								</div>
							</div>
						</>
					)}
					<Text as="h4" className="text-primary">
						{t('movement.containers_list')}
					</Text>
					<div className="w-full h-96 overflow-auto">
						<Table
							data={form.containers}
							columns={columns}
							className={
								'grid-cols-[repeat(9,minmax(140px,1fr))_auto]'
							}
						/>
					</div>
					<Text className="text-sm text-gray-label">
						{t('misc.total')}: {form.containers.length}
					</Text>
					<Button className="w-fit" onClick={() => addContainer()}>
						{t('movement.add_container')}
					</Button>
				</div>
				<Button
					className="place-self-end"
					type="submit"
					disabled={form.containers.length == 0}
				>
					{t('misc.save')}
				</Button>
			</form>
		</Card>
	);
};

export default MovementAuto;
